import React, { useState } from "react";
import styled from "@emotion/styled";
import IconCheck from "../../icons/IconCheck";
import { Gauge, Palette, Zap, Car, Settings, Clock, Check } from 'lucide-react';
import VehicleImagesCarousel from '../ui/VehicleImagesCarousel';

const ACCENT = "#F43F60";
const ACCEPTED = "#22C55E";
const COUNTERED = "#F59E42";
const HEADER_BG = "rgba(255,255,255,0.03)";
const HEADER_BORDER = "rgba(255,255,255,0.10)";

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
`;

const CollapsibleItems = styled.div`
  width: 100%;
  background: #25253C;
  border-radius: 10px;
  font-family: 'Clash Grotesk', sans-serif;
  padding: 16px;
  
  @media (max-width: 768px) {
    padding: 12px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    border-radius: 8px;
  }
`;

const CollapsibleItem = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 10px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 8px;
  }
`;

const CollapsibleHeader = styled.button`
  width: 100%;
  background: none;
  border: none;
  outline: none;
  color: #fff;
  text-align: left;
  cursor: pointer;
  position: relative;
  transition: all 0.18s;
  padding: 0;
  margin: 0;
  
  &:hover, &:focus {
    .header-content {
      background: #1E1E35;
    }
  }
  
  &.active {
    .header-content {
      background: #1E1E35;
    }
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  background: ${HEADER_BG};
  border-radius: 12px;
  padding: 8px 56px 8px 14px;
  min-height: 56px;
  position: relative;
  width: 100%;
  
  @media (max-width: 1024px) {
    gap: 14px;
    padding: 7px 40px 7px 13px;
    min-height: 52px;
    align-items: flex-start;
  }
  
  @media (max-width: 768px) {
    gap: 12px;
    padding: 6px 30px 6px 12px;
    min-height: 48px;
    border-radius: 10px;
    align-items: flex-start;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
    padding: 4px 30px 4px 8px;
    min-height: 44px;
    border-radius: 8px;
    flex-wrap: wrap;
    align-items: flex-start;
  }
 `;

const CollapsibleArrow = styled.span`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%) rotate(${props => (props.open ? 90 : 0)}deg);
  transition: transform 0.2s;
  color: #F43F60;
  font-size: 0.8em;
  
  @media (max-width: 768px) {
    right: 12px;
  }
  
  @media (max-width: 480px) {
    right: 8px;
  }
`;

const CollapsiblePanel = styled.div`
  background: #1E1E35;
  color: #fff;
  border-radius: 8px;
  margin-top: 8px;
  padding: ${props => (props.open ? '15px' : '0')};
  font-size: 1rem;
  font-weight: 400;
  max-height: ${props => (props.open ? '1000px' : '0')};
  overflow: hidden;
  transition: max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${props => (props.open ? 1 : 0)};
  pointer-events: ${props => (props.open ? 'auto' : 'none')};
  
  @media (max-width: 768px) {
    padding: 12px;
    margin-top: 6px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    margin-top: 4px;
    border-radius: 4px;
  }
`;

const CompactHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  background: ${HEADER_BG};
  border-radius: 12px;
  padding: 8px 14px;
  border: 1px solid ${HEADER_BORDER};
  min-height: 56px;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    gap: 12px;
    padding: 6px 12px;
    min-height: 48px;
    margin-bottom: 6px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
    padding: 4px 8px;
    min-height: 44px;
    margin-bottom: 4px;
  }
`;

const VehicleImg = styled.img`
  width: 56px;
  height: 42px;
  object-fit: cover;
  border-radius: 8px;
 background: #313151;
  
  @media (max-width: 768px) {
    width: 48px;
    height: 36px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 30px;
    border-radius: 4px;
  }
`;

const VehicleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
  overflow: hidden;
  
  @media (max-width: 3001px) {
    gap: 1px;
    overflow: visible;
  }
  
  @media (max-width: 768px) {
    gap: 1px;
    overflow: visible;
  }
  
  @media (max-width: 480px) {
    gap: 1px;
    overflow: visible;
  }
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.08rem;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  
  @media (max-width: 3001px) {
    font-size: 1rem;
    gap: 6px;
    flex-wrap: wrap;
    white-space: normal;
    line-height: 1.2;
    overflow: visible;
    text-overflow: unset;
  }
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    gap: 5px;
    flex-wrap: wrap;
    white-space: normal;
    line-height: 1.2;
    overflow: visible;
    text-overflow: unset;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    gap: 4px;
    flex-wrap: wrap;
    white-space: normal;
    line-height: 1.1;
    overflow: visible;
    text-overflow: unset;
  }
`;

const Trim = styled.span`
  color: #aaa;
  font-size: 16px;
  font-weight: 500;
  margin-left: 6px;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-left: 4px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    margin-left: 2px;
  }
`;

const Mileage = styled.span`
  color: #aaa;
  font-size: 16px;
  font-weight: 500;
  margin-left: 6px;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-left: 4px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin-left: 2px;
  }
`;

const ColorIndicator = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${props => props.color || '#888'};
  border: 2px solid #232325;
  margin-right: 4px;
  vertical-align: middle;
  
  @media (max-width: 480px) {
    width: 12px;
    height: 12px;
    margin-right: 2px;
  }
`;

const Price = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.status === 'accepted' ? ACCEPTED : props.status === 'countered' ? COUNTERED : ACCENT};
  min-width: 120px;
  text-align: right;
  margin-bottom: 3px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    min-width: 100px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    min-width: 80px;
  }
`;

const Time = styled.span`
  color: #aaa;
  font-size: 0.98rem;
  font-weight: 500;
  min-width: 80px;
  text-align: right;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    min-width: 70px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    min-width: 60px;
  }
`;

const TimelineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0 0 0 8px;
  
  @media (max-width: 768px) {
    margin: 0 0 0 6px;
  }
  
  @media (max-width: 480px) {
    margin: 0 0 0 4px;
  }
`;

const TimelineGroupLabel = styled.div`
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  margin: 18px 0 6px 0;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin: 14px 0 4px 0;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin: 10px 0 3px 0;
  }
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: flex-start;
  position: relative;
  min-height: 56px;
  margin-bottom: 0;
  
  @media (max-width: 768px) {
    min-height: 48px;
  }
  
  @media (max-width: 480px) {
    min-height: 40px;
  }
`;

const Dot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${props => props.color || '#363636'};
  border: 2px solid #232325;
  margin-right: 12px;
  margin-top: 18px;
  z-index: 2;
  
  @media (max-width: 768px) {
    width: 12px;
    height: 12px;
    margin-right: 10px;
    margin-top: 14px;
  }
  
  @media (max-width: 480px) {
    width: 10px;
    height: 10px;
    margin-right: 8px;
     margin-top: 12px;
  }
`;

const Line = styled.div`
  position: absolute;
  left: 6px;
  top: 26px;
  width: 2px;
  height: calc(100% - 2px);
  background: #363636;
  z-index: 1;
  
  @media (max-width: 768px) {
    left: 5px;
    top: 14px;
  }
  
  @media (max-width: 480px) {
    left: 4px;
    top: 12px;
  }
`;

const TimelineContent = styled.div`
  background: #1E1E35;
  border-radius: 8px;
  padding: 10px 16px;
  color: #fff;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 0.98rem;
  min-width: 160px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 0.9rem;
    min-width: 140px;
    margin-bottom: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 8px;
    font-size: 0.8rem;
    min-width: 120px;
    margin-bottom: 4px;
  }
`;

const TimelinePrice = styled.div`
  font-weight: 600;
  color: ${props => {
    if (props.status === 'accepted') return ACCEPTED;
    if (props.status === 'countered') return COUNTERED;
    if (props.status === 'pending') return ACCENT; // gray for pending
    return ACCENT;
  }};
  margin-right: 12px;
  
  @media (max-width: 768px) {
    margin-right: 8px;
  }
  
  @media (max-width: 480px) {
    margin-right: 6px;
  }
`;

const TimelineStatus = styled.div`
  color: #fff;
  font-size: 0.95rem;
  font-weight: 500;
  opacity: 0.8;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const TimelineTime = styled.div`
  color: #aaa;
  font-size: 16px;
  font-weight: 400;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

// BADGES (copied from InventoryTable.js, but as styled components here)
const MileageBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(107, 114, 128, 0.2);
  color: #9E9E9E;
  font-weight: 500;
  font-size: 16px;
  border-radius: 6px;
  padding: 2px 6px;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 1px 4px;
    gap: 2px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
    padding: 1px 3px;
  }
`;

const CertificationBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(139, 92, 246, 0.2);
  color: #8B5CF6;
  font-weight: 600;
  font-size: 12px;
  border-radius: 6px;
  padding: 2px 6px;
  text-transform: uppercase;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 12px;
    padding: 1px 4px;
    gap: 2px;
  }
  
`;

const EngineBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(59, 130, 246, 0.2);
  color: #3B82F6;
  font-weight: 500;
  font-size: 16px;
  border-radius: 6px;
  padding: 2px 6px;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 1px 4px;
    gap: 2px;
  }
  
`;

const TransmissionBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(245, 158, 11, 0.2);
  color: #F59E0B;
  font-weight: 500;
  font-size: 16px;
  border-radius: 6px;
  padding: 2px 6px;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 1px 4px;
    gap: 2px;
  }
  

`;

const ColorBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(236, 72, 153, 0.2);
  color: #EC4899;
  font-weight: 500;
  font-size: 16px;
  border-radius: 6px;
  padding: 2px 6px;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 1px 4px;
    gap: 2px;
  }

`;

const MPGBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(34, 197, 94, 0.2);
  color: #22C55E;
  font-weight: 500;
  font-size: 16px;
  border-radius: 6px;
  padding: 2px 6px;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 1px 4px;
    gap: 2px;
  }

`;

const UsedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(244, 63, 96, 0.2);
  color: #F43F60;
  font-weight: 600;
  font-size: 16px;
  border-radius: 6px;
  padding: 2px 6px;
  text-transform: uppercase;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 1px 4px;
    gap: 2px;
  }
  
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    switch (props.status) {
      case 'accepted': return 'rgba(34, 197, 94, 0.2)';
      case 'countered': return 'rgba(245, 158, 11, 0.2)';
      case 'pending': return 'rgba(244, 63, 96, 0.2)'; // accent color for pending
      default: return 'rgba(107, 114, 128, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'accepted': return '#22C55E';
      case 'countered': return '#F59E0B';
      case 'pending': return '#F43F60'; // accent color for pending
      default: return '#6B7280';
    }
  }};
  
  @media (max-width: 768px) {
    padding: 3px 10px;
    font-size: 0.7rem;
  }
  
  @media (max-width: 480px) {
    padding: 2px 8px;
    font-size: 0.65rem;
  }
`;

const AskingPriceBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(107, 114, 128, 0.18);
  color: #9E9E9E;
  font-weight: 600;
  font-size: 14px;
  border-radius: 8px;
  padding: 2px 8px;
  margin-left: 8px;
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 1px 6px;
    margin-left: 6px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
    padding: 1px 4px;
    margin-left: 4px;
  }
`;

const BadgesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 6px 0;
  max-width: 100%;
  
  @media (max-width: 768px) {
    gap: 6px;
    margin: 4px 0;
  }
  
  /* iPad specific adjustments */
  @media (min-width: 768px) and (max-width: 1023px) {
    gap: 6px;
    margin: 4px 0;
  }
  
  @media (max-width: 480px) {
    gap: 4px;
    margin: 3px 0;
  }
`;

const PriceTimeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 200px;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    gap: 8px;
    min-width: 160px;
  }
  
  @media (max-width: 480px) {
    gap: 6px;
    min-width: 120px;
    flex-direction: column;
    align-items: flex-end;
  }
`;

const StepsCount = styled.span`
  color: #aaa;
  font-size: 14px;
  margin-right: 8px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 12px;
    margin-right: 6px;
  }
  
  @media (max-width: 480px) {
    font-size: 11px;
    margin-right: 4px;
  }
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const TimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  
  @media (max-width: 480px) {
    gap: 2px;
  }
`;

const MSRPBadge = styled.div`
  color: #A855F7;
  font-size: 12px;
  font-weight: 600;
  background: rgba(168, 85, 247, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid rgba(168, 85, 247, 0.2);
  
  @media (max-width: 768px) {
    font-size: 11px;
    padding: 1px 4px;
  }
  
  @media (max-width: 480px) {
    font-size: 10px;
    padding: 1px 3px;
  }
`;

const UserInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  padding: 8px 12px;
  background: #25253C;
  border-radius: 6px;
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    gap: 3px;
    margin-top: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 4px 8px;
    gap: 2px;
    margin-top: 4px;
  }
`;

const UserInfoTitle = styled.div`
  color: #ededed;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 2px;
  
  @media (max-width: 768px) {
     font-size: 14px;
  }
  
`;

const UserInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #ededed;
  font-size: 0.85rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    gap: 4px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    gap: 3px;
  }
`;

const UserInfoLabel = styled.span`
  color: #9E9E9E;
  font-weight: 500;
  min-width: 50px;
  font-size: 14px;
  
  @media (max-width: 480px) {
    min-width: 40px;
  }
`;

const UserInfoValue = styled.span`
  color: #ededed;
  font-weight: 400;
  font-size: 14px;
`;

const ClickableUserInfoValue = styled.span`
  color: #ededed;
  font-weight: 400;
  cursor: pointer;
  font-size: 16px;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s ease;
  user-select: text;
  
  &:hover {
    background: rgba(244, 63, 96, 0.15);
    color: #F43F60;
  }
  
  &:active {
    background: #0fc9a7;
  }
  
  @media (max-width: 768px) {
    padding: 1px 3px;
  }
  
  @media (max-width: 480px) {
    padding: 1px 2px;
  }
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  color: #F43F60;
  cursor: pointer;
  padding: 2px 6px;
  font-size: 16px;
  border-radius: 4px;
  margin-left: 4px;
  transition: all 0.2s ease;
  
  &:active {
    background: #0fc9a7;
  }
  
  @media (max-width: 768px) {
    padding: 1px 4px;
    font-size: 0.7rem;
  }
`;

const ClickableUserInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #ededed;
  font-size: 16px;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    gap: 4px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    gap: 3px;
  }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Toast = styled.div`
  background: #22C55E;
  color: #ffffff;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 14px;
  font-weight: 500;
  min-width: 200px;
  max-width: 300px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @media (max-width: 768px) {
    min-width: 180px;
    max-width: 280px;
    padding: 10px 14px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    min-width: 160px;
    max-width: 260px;
    padding: 8px 12px;
    font-size: 11px;
  }
`;

function formatDateLabel(date) {
  const now = new Date();
  const d = new Date(date);
  const isToday = now.toDateString() === d.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = yesterday.toDateString() === d.toDateString();
  if (isToday) return "Today";
  if (isYesterday) return "Yesterday";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatTime(date) {
  return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function groupByDate(items) {
  // items are already sorted newest to oldest
  const groups = [];
  let lastDate = null;
  items.forEach(item => {
    const d = new Date(item.createdAt);
    const dateKey = d.toDateString();
    if (!groups.length || groups[groups.length - 1].dateKey !== dateKey) {
      groups.push({ dateKey, date: d, items: [item] });
    } else {
      groups[groups.length - 1].items.push(item);
    }
  });
  return groups;
}

function getDotColor(status) {
  if (status === 'accepted') return ACCEPTED;
  if (status === 'countered') return COUNTERED;
  return ACCENT;
}

function OfferTimelines({
  offers = []
}) {
  const [openIndexes, setOpenIndexes] = useState([0]);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselImages, setCarouselImages] = useState([]);
  const [carouselTitle, setCarouselTitle] = useState('');
  const [carouselInitialIndex, setCarouselInitialIndex] = useState(0);
  const [toast, setToast] = useState(null);

  const handleToggle = idx => {
    setOpenIndexes(open => (open[0] === idx ? [] : [idx]));
  };

  const handleImageClick = (images, title, initialIndex = 0) => {
    setCarouselImages(images);
    setCarouselTitle(title);
    setCarouselInitialIndex(initialIndex);
    setCarouselOpen(true);
  };

  const handleCloseCarousel = () => {
    setCarouselOpen(false);
    setCarouselImages([]);
    setCarouselTitle('');
    setCarouselInitialIndex(0);
  };

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => {
      setToast(null);
    }, 3000);
  };

  const handleCopyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${type} copied successfully!`);
      console.log(`${type} copied to clipboard:`, text);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showToast(`${type} copied successfully!`);
      console.log(`${type} copied to clipboard (fallback):`, text);
    }
  };

  const handleEmailClick = (email) => {
    if (email) {
      window.open(`mailto:${email}`, '_blank');
    }
  };

  const handlePhoneClick = (phone) => {
    if (phone) {
      window.open(`tel:${phone}`, '_blank');
    }
  };

  return (
    <Container>
      {/* Toast Notification */}
      {toast && (
        <ToastContainer>
          <Toast>
            <Check size={16} />
            {toast}
          </Toast>
        </ToastContainer>
      )}
      
      <CollapsibleItems>
        {offers.map((offer, idx) => {
          const vehicle = offer.vehicle || {};
          const timeline = offer.timeline || [];
          const user = offer.user || {};
          const userInfo = user.userInfo || {};
          
          // If no timeline exists, create a single timeline item from the offer itself
          const effectiveTimeline = timeline.length > 0 ? timeline : [{
            id: offer.id,
            status: offer.status,
            amount: offer.amount,
            createdAt: offer.created_at || offer.createdAt
          }];
          
          const groups = groupByDate(effectiveTimeline);
          const price = offer.amount;
          const priceStatus = offer.status;
          const newestTime = effectiveTimeline[0]?.createdAt || offer.created_at || offer.createdAt;
          const isOpen = openIndexes.includes(idx);
          const stepsCount = effectiveTimeline.length;

          return (
            <CollapsibleItem key={idx}>
              <CollapsibleHeader
                className={isOpen ? 'active' : ''}
                onClick={() => handleToggle(idx)}
                aria-expanded={isOpen}
              >
                <HeaderContent className="header-content">
                  {/* Vehicle image thumbnail with click to open carousel */}
                  {vehicle.images && vehicle.images.length > 0 ? (
                    <div style={{ cursor: 'pointer' }} onClick={e => { e.stopPropagation(); handleImageClick(vehicle.images, `${vehicle.year} ${vehicle.make} ${vehicle.model} - ${vehicle.trim || ''}`); }}>
                      <VehicleImg
                        src={vehicle.images[0].url}
                        alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                        onError={e => { e.target.src = '/car-placeholder.png'; }}
                      />
                    </div>
                  ) : (
                    <div style={{ width: '56px', height: '42px', background: '#313151', borderRadius: '8px' }} />
                  )}
                  <VehicleInfo>
                    <NameRow>
                      {vehicle.year} {vehicle.make} {vehicle.model}
                      {vehicle.trim && <Trim>{vehicle.trim}</Trim>}
                      {vehicle.driveTrain && <Trim>{vehicle.driveTrain}</Trim>}
                      {vehicle.stockId && <Trim>Stock: {vehicle.stockId}</Trim>}
                      {vehicle.vin && <Trim>VIN: {vehicle.vin}</Trim>}
                    </NameRow>
                    <BadgesContainer>
                      {vehicle.isUsed && <UsedBadge><Car size={12} />Used</UsedBadge>}
                      {vehicle.engine && <EngineBadge><Settings size={10} />{vehicle.engine}</EngineBadge>}
                      {vehicle.transmission && <TransmissionBadge><Gauge size={10} />{vehicle.transmission}</TransmissionBadge>}
                      {vehicle.mileage && <MileageBadge><Car size={12} />{vehicle.mileage.toLocaleString()} mi</MileageBadge>}
                      {vehicle.isCertified && <CertificationBadge>Certified</CertificationBadge>}
                      {vehicle.mpgCity || vehicle.mpgHighway ? <MPGBadge><Zap size={10} />{vehicle.mpgCity || '--'}/{vehicle.mpgHighway || '--'}</MPGBadge> : null}
                      {vehicle.exteriorColor && <ColorBadge><Palette size={10} />Ext: {vehicle.exteriorColor}</ColorBadge>}
                      {vehicle.interiorColor && <ColorBadge><Palette size={10} />Int: {vehicle.interiorColor}</ColorBadge>}
                    </BadgesContainer>
                  </VehicleInfo>
                  <PriceTimeContainer>
                    <StepsCount>
                      {stepsCount} step{stepsCount !== 1 ? 's' : ''}
                    </StepsCount>
                    <PriceContainer>
                      <Price status={priceStatus}>
                        {priceStatus === 'accepted' && <IconCheck size={16} color={ACCEPTED} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
                        {priceStatus === 'countered' && <span style={{ fontWeight: 700, marginRight: 4 }}>↺</span>}
                        {priceStatus === 'pending' && <Clock size={16} color={ACCENT} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
                        {price ? `$${price.toLocaleString()}` : '--'}
                      </Price>
                      {vehicle.askingPrice && vehicle.askingPrice !== price && (
                        <AskingPriceBadge>
                          Asking: ${vehicle.askingPrice.toLocaleString()}
                        </AskingPriceBadge>
                      )}
                    </PriceContainer>
                    <TimeContainer>
                      <Time>{formatTime(newestTime)}</Time>
                      {vehicle.msrp && (
                        <MSRPBadge>
                          MSRP: ${vehicle.msrp.toLocaleString()}
                        </MSRPBadge>
                      )}
                    </TimeContainer>
                    <CollapsibleArrow open={isOpen}>▶</CollapsibleArrow>
                  </PriceTimeContainer>
                </HeaderContent>
              </CollapsibleHeader>
              <CollapsiblePanel open={isOpen}>
                {/* User Information Section */}
                {userInfo && (userInfo.name || userInfo.email || userInfo.phone) && (
                  <UserInfoContainer>
                    <UserInfoTitle>🙍‍♂️ Customer Information</UserInfoTitle>
                    {userInfo.name && (
                      <UserInfoItem>
                        <UserInfoLabel>Name:</UserInfoLabel>
                        <UserInfoValue>{userInfo.name}</UserInfoValue>
                      </UserInfoItem>
                    )}
                    {userInfo.email && (
                      <ClickableUserInfoItem>
                        <UserInfoLabel>Email:</UserInfoLabel>
                        <ClickableUserInfoValue 
                          onClick={() => handleEmailClick(userInfo.email)}
                          title="Click to open email client"
                        >
                          {userInfo.email}
                        </ClickableUserInfoValue>
                        <CopyButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyToClipboard(userInfo.email, 'Email');
                          }}
                          title="Copy email to clipboard"
                        >
                          📋
                        </CopyButton>
                      </ClickableUserInfoItem>
                    )}
                    {userInfo.phone && (
                      <ClickableUserInfoItem>
                        <UserInfoLabel>Phone:</UserInfoLabel>
                        <ClickableUserInfoValue 
                          onClick={() => handlePhoneClick(userInfo.phone)}
                          title="Click to call"
                        >
                          {userInfo.phone}
                        </ClickableUserInfoValue>
                        <CopyButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyToClipboard(userInfo.phone, 'Phone');
                          }}
                          title="Copy phone to clipboard"
                        >
                          📋
                        </CopyButton>
                      </ClickableUserInfoItem>
                    )}
                  </UserInfoContainer>
                )}
                
                <TimelineWrapper>
                  {groups.map((group, gidx) => (
                    <React.Fragment key={group.dateKey}>
                      <TimelineGroupLabel>{formatDateLabel(group.date)}</TimelineGroupLabel>
                      {group.items.map((item, idx) => {
                        const isLast = idx === group.items.length - 1 && gidx === groups.length - 1;
                        return (
                          <TimelineItem key={item.id || idx}>
                            <Dot color={getDotColor(item.status)} />
                            {!isLast && <Line />}
                            <TimelineContent>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', gap: '12px' }}>
                                <TimelinePrice status={item.status}>
                                  {item.status === 'accepted' && <IconCheck size={14} color={ACCEPTED} style={{ marginRight: 3, verticalAlign: 'middle' }} />}
                                  {item.status === 'countered' && <span style={{ fontWeight: 700, marginRight: 3 }}>↺</span>}
                                  {item.amount ? `$${item.amount.toLocaleString()}` : '--'}
                                </TimelinePrice>
                                <StatusBadge status={item.status}>
                                  {item.status || '--'}
                                </StatusBadge>
                              </div>
                              <TimelineTime>{formatTime(item.createdAt)}</TimelineTime>
                              {item.appointmentId && <div style={{ color: '#F43F60', fontSize: '14px', marginTop: '4px' }}>📅 Appointment scheduled</div>}
                            </TimelineContent>
                          </TimelineItem>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </TimelineWrapper>
              </CollapsiblePanel>
            </CollapsibleItem>
          );
        })}
      </CollapsibleItems>
      {/* Vehicle Images Carousel Modal */}
      <VehicleImagesCarousel
        images={carouselImages}
        isOpen={carouselOpen}
        onClose={handleCloseCarousel}
        title={carouselTitle}
        initialIndex={carouselInitialIndex}
      />
    </Container>
  );
}

export default OfferTimelines;